import type { Artist, Voicebank, Song, AppData, Playlist, User } from '../types';

interface ErrorWithDetails extends Error {
  cause?: Error;
  stack?: string;
}

export async function fetchArtists(): Promise<Artist[]> {
  try {
    // Use API base URL with fallback for development
    const apiBase = import.meta.env.VITE_API_BASE || '/api';
    const response = await fetch(`${apiBase}/artists`, { cache: 'no-store' });
    
    console.log('Artists Fetch Response:', {
      status: response.status,
      headers: Object.fromEntries(response.headers.entries()),
      url: response.url
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Fetch Error Response:', errorText);
      throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
    }

    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const text = await response.text();
      console.error('Non-JSON Response:', text);
      throw new Error(`Expected JSON, got ${contentType}`);
    }

    const data = await response.json();
    return data.artists;
  } catch (error) {
    const err = error as ErrorWithDetails;
    console.error('Fetch Artists Error:', {
      name: err.name,
      message: err.message,
      stack: err.stack
    });
    throw err;
  }
}

export async function fetchVoicebanks(): Promise<Voicebank[]> {
  try {
    const apiBase = import.meta.env.VITE_API_BASE || '/api';
    const response = await fetch(`${apiBase}/voicebanks`, { cache: 'no-store' });
    
    console.log('Voicebanks Fetch Response:', {
      status: response.status,
      headers: Object.fromEntries(response.headers.entries()),
      url: response.url
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Fetch Error Response:', errorText);
      throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
    }

    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const text = await response.text();
      console.error('Non-JSON Response:', text);
      throw new Error(`Expected JSON, got ${contentType}`);
    }

    const data = await response.json();
    return data.synths;
  } catch (error) {
    const err = error as ErrorWithDetails;
    console.error('Fetch Voicebanks Error:', {
      name: err.name,
      message: err.message,
      stack: err.stack
    });
    throw err;
  }
}

export async function fetchSongs(): Promise<Song[]> {
  try {
    const apiBase = import.meta.env.VITE_API_BASE || '/api';
    const response = await fetch(`${apiBase}/songs`, { cache: 'no-store' });
    
    console.log('Songs Fetch Response:', {
      status: response.status,
      headers: Object.fromEntries(response.headers.entries()),
      url: response.url
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Fetch Error Response:', errorText);
      throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
    }

    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const text = await response.text();
      console.error('Non-JSON Response:', text);
      throw new Error(`Expected JSON, got ${contentType}`);
    }

    const data = await response.json();
    return data.songs;
  } catch (error) {
    const err = error as ErrorWithDetails;
    console.error('Fetch Songs Error:', {
      name: err.name,
      message: err.message,
      stack: err.stack
    });
    throw err;
  }
}

export async function fetchAllData(): Promise<AppData> {
  try {
    const [artists, voicebanks, songs] = await Promise.all([
      fetchArtists(),
      fetchVoicebanks(),
      fetchSongs(),
    ]);
    return { artists, voicebanks, songs };
  } catch (error) {
    const err = error as ErrorWithDetails;
    throw err;
  }
}

// New function to fetch albums from D1
export async function fetchAlbums(): Promise<any[]> {
  try {
    const apiBase = import.meta.env.VITE_API_BASE || '/api';
    const response = await fetch(`${apiBase}/albums`, { cache: 'no-store' });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Fetch Error Response:', errorText);
      throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
    }

    const data = await response.json();
    return data.albums;
  } catch (error) {
    const err = error as ErrorWithDetails;
    console.error('Fetch Albums Error:', {
      name: err.name,
      message: err.message,
      stack: err.stack
    });
    throw err;
  }
}

// Function to fetch a specific album with its songs
export async function fetchAlbumWithSongs(albumId: string): Promise<{album: any, songs: Song[]}> {
  try {
    const apiBase = import.meta.env.VITE_API_BASE || '/api';
    const response = await fetch(`${apiBase}/album?id=${albumId}`, { cache: 'no-store' });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Fetch Error Response:', errorText);
      throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    const err = error as ErrorWithDetails;
    console.error('Fetch Album Error:', {
      name: err.name,
      message: err.message,
      stack: err.stack
    });
    throw err;
  }
}

// Local storage keys
const STORAGE_KEYS = {
  FAVORITES: 'naia_favorites',
  RECENT_PLAYS: 'naia_recent_plays',
  VOLUME: 'naia_volume',
  PLAYBACK_STATE: 'naia_playback_state',
  AUTH_TOKEN: 'auth_token',
} as const;

// Fetch user playlists from API
export async function fetchPlaylists(): Promise<Playlist[]> {
  try {
    const token = storage.getAuthToken();
    if (!token) return [];
    
    const apiBase = import.meta.env.VITE_API_BASE || '/api';
    const response = await fetch(`${apiBase}/playlists`, { 
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      cache: 'no-store' 
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Fetch Playlists Error Response:', errorText);
      throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
    }

    const data = await response.json();
    return data.playlists || [];
  } catch (error) {
    const err = error as ErrorWithDetails;
    console.error('Fetch Playlists Error:', {
      name: err.name,
      message: err.message,
      stack: err.stack
    });
    return [];
  }
}

export async function fetchPlaylist(playlistId: string): Promise<Playlist> {
  try {
    const token = storage.getAuthToken();
    if (!token) throw new Error('Authentication required');
    
    const apiBase = import.meta.env.VITE_API_BASE || '/api';
    const response = await fetch(`${apiBase}/playlists?playlistId=${playlistId}`, { 
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      cache: 'no-store' 
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Fetch Playlist Error Response:', errorText);
      throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
    }

    const data = await response.json();
    return data.playlist;
  } catch (error) {
    const err = error as ErrorWithDetails;
    console.error('Fetch Playlist Error:', {
      name: err.name,
      message: err.message,
      stack: err.stack
    });
    throw err;
  }
}

export async function createPlaylist(name: string, description?: string, isPublic: boolean = false): Promise<Playlist> {
  try {
    const token = storage.getAuthToken();
    if (!token) throw new Error('Authentication required');
    
    const apiBase = import.meta.env.VITE_API_BASE || '/api';
    const response = await fetch(`${apiBase}/playlists`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        name, 
        description: description || null, 
        is_public: isPublic 
      }),
    });
    
    if (!response.ok) {
      throw new Error('Failed to create playlist');
    }
    
    const data = await response.json();
    return data.playlist;
  } catch (error) {
    console.error('Create playlist error:', error);
    throw error;
  }
}

export async function updatePlaylist(playlistId: string, updates: Partial<Playlist>): Promise<Playlist> {
  try {
    const token = storage.getAuthToken();
    if (!token) throw new Error('Authentication required');
    
    const apiBase = import.meta.env.VITE_API_BASE || '/api';
    const response = await fetch(`${apiBase}/playlists?playlistId=${playlistId}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updates),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Update Playlist Error Response:', errorText);
      throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
    }

    const data = await response.json();
    return data.playlist;
  } catch (error) {
    const err = error as ErrorWithDetails;
    console.error('Update Playlist Error:', {
      name: err.name,
      message: err.message,
      stack: err.stack
    });
    throw err;
  }
}

export async function deletePlaylist(playlistId: string): Promise<void> {
  try {
    const token = storage.getAuthToken();
    if (!token) throw new Error('Authentication required');
    
    const apiBase = import.meta.env.VITE_API_BASE || '/api';
    const response = await fetch(`${apiBase}/playlists?playlistId=${playlistId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Delete Playlist Error Response:', errorText);
      throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
    }
  } catch (error) {
    const err = error as ErrorWithDetails;
    console.error('Delete Playlist Error:', {
      name: err.name,
      message: err.message,
      stack: err.stack
    });
    throw err;
  }
}

export async function addSongToPlaylist(playlistId: string, songId: string, position?: number): Promise<void> {
  try {
    const token = storage.getAuthToken();
    if (!token) throw new Error('Authentication required');
    
    const apiBase = import.meta.env.VITE_API_BASE || '/api';
    const response = await fetch(`${apiBase}/playlists/songs`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ playlistId, songId, position }),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Add Song to Playlist Error Response:', errorText);
      throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
    }
  } catch (error) {
    const err = error as ErrorWithDetails;
    console.error('Add Song to Playlist Error:', {
      name: err.name,
      message: err.message,
      stack: err.stack
    });
    throw err;
  }
}

export async function removeSongFromPlaylist(playlistId: string, songId: string): Promise<void> {
  try {
    const token = storage.getAuthToken();
    if (!token) throw new Error('Authentication required');
    
    const apiBase = import.meta.env.VITE_API_BASE || '/api';
    const response = await fetch(`${apiBase}/playlists/songs`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ playlistId, songId }),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Remove Song from Playlist Error Response:', errorText);
      throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
    }
  } catch (error) {
    const err = error as ErrorWithDetails;
    console.error('Remove Song from Playlist Error:', {
      name: err.name,
      message: err.message,
      stack: err.stack
    });
    throw err;
  }
}

// Get changelog entries
export async function fetchChangelog(): Promise<any[]> {
  try {
    const apiBase = import.meta.env.VITE_API_BASE || '/api';
    const response = await fetch(`${apiBase}/changelog`, { cache: 'no-store' });
    
    console.log('Changelog Fetch Response:', {
      status: response.status,
      headers: Object.fromEntries(response.headers.entries()),
      url: response.url
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Fetch Error Response:', errorText);
      throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
    }

    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const text = await response.text();
      console.error('Non-JSON Response:', text);
      throw new Error(`Expected JSON, got ${contentType}`);
    }

    const data = await response.json();
    return data.changelog;
  } catch (error) {
    const err = error as ErrorWithDetails;
    console.error('Fetch Changelog Error:', {
      name: err.name,
      message: err.message,
      stack: err.stack
    });
    throw err;
  }
}

export const storage = {
  getFavorites: (): string[] => 
    JSON.parse(localStorage.getItem(STORAGE_KEYS.FAVORITES) || '[]'),
  
  setFavorites: (favorites: string[]) =>
    localStorage.setItem(STORAGE_KEYS.FAVORITES, JSON.stringify(favorites)),
  
  getRecentPlays: (): string[] =>
    JSON.parse(localStorage.getItem(STORAGE_KEYS.RECENT_PLAYS) || '[]'),
  
  setRecentPlays: (recentPlays: string[]) =>
    localStorage.setItem(STORAGE_KEYS.RECENT_PLAYS, JSON.stringify(recentPlays)),
  
  getVolume: (): number =>
    parseFloat(localStorage.getItem(STORAGE_KEYS.VOLUME) || '1'),
  
  setVolume: (volume: number) =>
    localStorage.setItem(STORAGE_KEYS.VOLUME, volume.toString()),
  
  getAuthToken: (): string | null =>
    localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN),
  
  setAuthToken: (token: string) =>
    localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token),
  
  removeAuthToken: () =>
    localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN),
}; 

// Authentication API
export const auth = {
  // Register a new user
  register: async (email: string, username: string, password: string, displayName?: string): Promise<{ user: User, token: string }> => {
    const apiBase = import.meta.env.VITE_API_BASE || '/api';
    const response = await fetch(`${apiBase}/auth/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, username, password, displayName }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Registration failed');
    }
    
    return await response.json();
  },
  
  // Login user
  login: async (email: string, password: string): Promise<{ user: User, token: string }> => {
    const apiBase = import.meta.env.VITE_API_BASE || '/api';
    const response = await fetch(`${apiBase}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Login failed');
    }
    
    const data = await response.json();
    storage.setAuthToken(data.token);
    return data;
  },
  
  // Logout user
  logout: async (): Promise<void> => {
    const token = storage.getAuthToken();
    if (token) {
      try {
        const apiBase = import.meta.env.VITE_API_BASE || '/api';
        await fetch(`${apiBase}/auth`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
      } catch (error) {
        console.error('Logout error:', error);
      }
    }
    storage.removeAuthToken();
  },
  
  // Get current user
  getCurrentUser: async (): Promise<User | null> => {
    const token = storage.getAuthToken();
    if (!token) return null;
    
    try {
      const apiBase = import.meta.env.VITE_API_BASE || '/api';
      const response = await fetch(`${apiBase}/auth`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          storage.removeAuthToken();
          return null;
        }
        throw new Error('Failed to get user data');
      }
      
      const data = await response.json();
      return data.user;
    } catch (error) {
      console.error('Get current user error:', error);
      return null;
    }
  },
};

// Public playlists API
export const publicPlaylists = {
  // Get a shared playlist by its share ID
  getByShareId: async (shareId: string): Promise<Playlist | null> => {
    try {
      const apiBase = import.meta.env.VITE_API_BASE || '/api';
      const response = await fetch(`${apiBase}/playlists/shared/${shareId}`, {
        cache: 'no-store'
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new Error('Failed to fetch shared playlist');
      }
      
      const data = await response.json();
      return data.playlist;
    } catch (error) {
      console.error('Fetch shared playlist error:', error);
      return null;
    }
  }
};

// Library API (formerly Favorites)
export const favorites = {
  // Fetch user playlists from API
  fetchPlaylists(): Promise<Playlist[]> {
    const token = storage.getAuthToken();
    if (!token) return Promise.resolve([]);
    
    return new Promise(async (resolve, reject) => {
      try {
        const apiBase = import.meta.env.VITE_API_BASE || '/api';
        const response = await fetch(`${apiBase}/playlists`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          cache: 'no-store'
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch playlists');
        }
        
        const data = await response.json();
        // Server returns playlists in the 'playlists' property
        resolve(data.playlists || []);
      } catch (error) {
        console.error('Fetch playlists error:', error);
        reject(error);
      }
    });
  },
  
  // Get user favorites
  getFavorites: async (): Promise<Song[]> => {
    try {
      const token = storage.getAuthToken();
      if (!token) return [];
      
      const apiBase = import.meta.env.VITE_API_BASE || '/api';
      console.log('Fetching library data from:', `${apiBase}/library`);
      
      const response = await fetch(`${apiBase}/library`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        cache: 'no-store'
      });
      
      console.log('Library API response status:', response.status);
      
      if (!response.ok) {
        // Try to get detailed error information
        let errorDetails = '';
        try {
          const errorData = await response.json();
          errorDetails = JSON.stringify(errorData);
        } catch (e) {
          errorDetails = await response.text();
        }
        
        console.error('Library API error details:', errorDetails);
        throw new Error(`Failed to fetch favorites: ${response.status} ${errorDetails}`);
      }
      
      const data = await response.json();
      console.log('Library API response data:', data);
      return data.songs || []; // The server returns 'songs' property, not 'favorites'
    } catch (error) {
      console.error('Library API error:', error);
      throw error;
    }
  },
  
  // Check if a song is favorited
  isFavorite: async (songId: string): Promise<boolean> => {
    const token = storage.getAuthToken();
    if (!token) {
      // Fall back to local storage for guests
      const favorites = storage.getFavorites();
      return favorites.includes(songId);
    }
    
    try {
      const apiBase = import.meta.env.VITE_API_BASE || '/api';
      const response = await fetch(`${apiBase}/library?songId=${songId}`, {
        method: 'HEAD',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      return response.ok;
    } catch (error) {
      console.error('Check favorite error:', error);
      return false;
    }
  },
  
  // Add a song to library
  addFavorite: async (songId: string): Promise<void> => {
    const token = storage.getAuthToken();
    if (!token) {
      // Fall back to local storage for guests
      const favorites = storage.getFavorites();
      if (!favorites.includes(songId)) {
        storage.setFavorites([...favorites, songId]);
      }
      return;
    }
    
    const apiBase = import.meta.env.VITE_API_BASE || '/api';
    const response = await fetch(`${apiBase}/library`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ songId }),
    });
    
    if (!response.ok) {
      throw new Error('Failed to add to library');
    }
  },
  
  // Remove a song from library
  removeFavorite: async (songId: string): Promise<void> => {
    const token = storage.getAuthToken();
    if (!token) {
      // Fall back to local storage for guests
      const favorites = storage.getFavorites();
      storage.setFavorites(favorites.filter(id => id !== songId));
      return;
    }
    
    const apiBase = import.meta.env.VITE_API_BASE || '/api';
    const response = await fetch(`${apiBase}/library?songId=${songId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!response.ok) {
      throw new Error('Failed to remove from library');
    }
  },
};